// ─────────────────────────────────────────────────────────────────────────────
// Types domaine TrafficPlay — frontend
// Miroir des modèles Prisma, adaptés pour la consommation React
// ─────────────────────────────────────────────────────────────────────────────

// ─── Enums ───────────────────────────────────────────────────────────────────

export type ProgramType =
  | 'EMISSION'
  | 'JINGLE' | 'COMING_NEXT'
  | 'BANDE_ANNONCE' | 'GENERIQUE_ANTENNE' | 'AVERTISSEMENT' | 'BILLBOARD'

export type MediaStatus =
  | 'OK' | 'MISSING' | 'INVALID_CODEC' | 'DURATION_MISMATCH' | 'UNCHECKED'

export type BugType = 
  | 'NONE' | 'LMTV_SARTHE' | 'LMTV_SARTHE_DIRECT' | 'LMTV_SARTHE_INEDIT'

export type GridType   = 'TEMPLATE' | 'WEEKLY'
export type FirstAirSource = 'MANUAL' | 'ASRUN'
export type AiringKind = 'PREMIERE' | 'RERUN' | 'STANDARD'
/** Un modèle ne pose que des inédits et leurs rediffusions, plus la case fixe. */
export type TemplateRuleType = 'FIXED_PROGRAM' | 'PREMIERE' | 'PREMIERE_RERUN' | 'SMART_RERUN' | 'FRESH_RERUN' | 'DESCENDING_RERUN' | 'CURATED' | 'MIRROR_RERUN'
export type SlotContentAlignment = 'START' | 'END'
export type GenerationScope = 'DAY' | 'WEEK'
export type GenerationDestination = 'CURRENT_GRID' | 'NEW_DRAFT'
export type GenerationStatus = 'PREVIEW' | 'APPLIED' | 'ROLLED_BACK' | 'PARTIALLY_ROLLED_BACK'
export type GridStatus = 'DRAFT' | 'VALIDATED' | 'ACTIVE' | 'ARCHIVED'

export type ItemType =
  | 'PROGRAM' | 'LIVE' | 'AD_BREAK' | 'INFOSERVICE' | 'JINGLE' | 'INTERLUDE' | 'FILLER'

export type ItemStatus =
  | 'SCHEDULED' | 'LOADING' | 'ON_AIR' | 'DONE' | 'GAP' | 'ERROR' | 'SKIPPED'

export type SourceType   = 'DECKLINK' | 'INTERNET' | 'RTMP' | 'RTSP' | 'SRT'
export type SourceStatus = 'ACTIVE' | 'INACTIVE' | 'SIGNAL_LOST' | 'UNSTABLE'

export type InterludeType = 'LOOP' | 'PROMO' | 'COMING_NEXT' | 'SLATE' | 'EMERGENCY'
export type GapScenario   =
  | 'FILE_SHORT' | 'FILE_LONG' | 'FILE_FREEZE' | 'FILE_MISSING'
  | 'FILE_CORRUPT' | 'AUDIO_SILENCE' | 'SIGNAL_LOST' | 'INTERLUDE_FAIL' | 'SPOT_DRIFT'

export type AsRunStatus    = 'OK' | 'GAP_FILLED' | 'OVERRUN' | 'ERROR' | 'MANUAL_OVERRIDE'
export type PlayoutState   = 'IDLE' | 'PLAYING' | 'GAP' | 'ERROR'
export type LiveStartMode  = 'AUTO' | 'MANUAL'
export type LiveEndMode     = 'AUTO' | 'MANUAL'
export type UserRole       = 'ADMIN' | 'JOURNALIST' | 'EDITOR' | 'TECHNICIAN' | 'ADS_INFOSERVICE'
export type RecurrenceFreq = 'DAILY' | 'WEEKLY' | 'MONTHLY'
export type ReplaceProgramScope = 'DAY' | 'NEXT_DAY' | 'WEEK' | 'MONTH' | 'UNTIL'

export type AiSuggestionPhase  = 'PROGRAMS' | 'ADS'
export type AiSuggestionStatus = 'PENDING' | 'PARTIAL' | 'APPLIED' | 'REJECTED'
export type CgOverlayAnchor = 'PROGRAM_START' | 'PROGRAM_END' | 'PROGRAM_DURING'
export type CgTemplateType = 'COMING_NEXT' | 'BUG_CHAINE' | 'LOWER_THIRD' | 'INTERLUDE' | 'CUSTOM'
export type CgDesignerElementType = 'TEXT' | 'SHAPE' | 'IMAGE' | 'VIDEO' | 'COUNTER'
export type CgDesignerAnimation =
  | 'NONE' | 'FADE' | 'SLIDE_LEFT' | 'SLIDE_RIGHT' | 'SLIDE_UP' | 'SLIDE_DOWN'
  | 'POP' | 'SCALE' | 'WIPE_LEFT' | 'WIPE_RIGHT' | 'ROTATE' | 'BLUR'
export type CgDesignerEasing = 'LINEAR' | 'EASE_IN' | 'EASE_OUT' | 'EASE_IN_OUT' | 'CIRC_OUT'
export type CgDesignerCounterMode = 'COUNTDOWN' | 'COUNTUP' | 'CLOCK'
export type CgDesignerCounterFormat = 'HH_MM_SS' | 'HH_MM' | 'MM_SS' | 'D_H_M_S' | 'DAYS' | 'MINUTES' | 'DATE_TIME'

// ─── Séries ───────────────────────────────────────────────────────────────────

export type ProgramOrigin = 'IN_HOUSE' | 'PURCHASED' | 'PARTNERSHIP'

export const PROGRAM_ORIGIN_LABELS: Record<ProgramOrigin, string> = {
  IN_HOUSE:    'Production interne',
  PURCHASED:   'Achat de droits',
  PARTNERSHIP: 'Partenariat',
}

// ─── Entités principales ──────────────────────────────────────────────────────

export interface Channel {
  id:        string
  name:      string
  slug:      string
  logoUrl:   string | null
  active:    boolean
  createdAt: string
  updatedAt: string
  manualEndJingleProgramId: string | null
  liveResumeJingleProgramId: string | null
  /** Jingles de liaison tirés au hasard aux jonctions créées par le recalage ou le dépôt d'un modèle. */
  junctionJingleProgramIds: string[]
  infoserviceCgTemplateId: string | null
  infoserviceCgOffsetSec: number
  infoserviceCgHideBeforeEndSec: number
}

export interface Program {
  id:            string
  channelId:     string
  title:         string
  titleShort:    string | null
  epgSubtitle:   string | null
  summary:       string | null
  type:          ProgramType
  programCode:   string | null
  durationSec:   number
  /** Genre hérité en texte libre. Plus saisi ni publié : voir `programGenreId`. */
  genre:         string | null
  /** Genre éditorial (liste fermée), publié dans `<genre>`. */
  programGenreId: string | null
  /** Précision libre sous le genre, publiée dans `<sous_categorie>`. */
  subGenre:      string | null
  tags:          string[]
  logoUrl:       string | null
  thumbnailUrl:  string | null
  director:      string | null
  presenter:     string | null
  production:    string | null
  year:          number | null
  bugType:       BugType
  logoProfileId: string | null
  /** Le fichier porte déjà un logo : aucun logo d'antenne par-dessus. */
  noLogo:        boolean
  isLive:        boolean
  liveSourceId:  string | null
  isFourThree:   boolean
  /** Épisode livré en deux versions (longue / courte). */
  dualVersion:   boolean
  isNew:         boolean
  active:        boolean
  adHoc:         boolean
  season:        number | null
  episode:       number | null
  origin:        ProgramOrigin | null
  seriesId:      string | null
  programCategoryId: string | null
  /** Type d'élément d'antenne, quand `type` désigne de l'habillage. */
  antennaElementTypeId?: string | null
  plannedFirstAirDate: string | null
  firstAiredAt:  string | null
  firstAiredSource: FirstAirSource | null
  legacyRerunCount: number
  parentProgramId: string | null
  partNumber: number
  partOverrideFields: Array<'title' | 'epgSubtitle' | 'summary' | 'tags' | 'thumbnailUrl'>
  // ── Valeurs publiées à l'EPG (Plurimedia / Media Press) ───────────────────
  // Le genre publié vient de `programGenre` (liste fermée) ; `subGenre` part
  // dans `<sous_categorie>`. `epgShowId` ne sert que de repli quand le
  // programme n'a pas de série.
  epgFederation: string | null
  epgThemeId:    number | null
  epgShowId:     number | null
  /** Fin des droits de diffusion, publiée en `JJ/MM/AA` dans le flux EPG. */
  epgRightsEndDate: string | null
  interPartBreakSec: number
  createdAt:     string
  updatedAt:     string
  // Relations optionnelles (incluses selon le endpoint)
  mediaAssets?:  MediaAsset[]
  liveSource?:   LiveSource | null
  channel?:      Pick<Channel, 'id' | 'name' | 'slug'>
  series?:       Pick<Series, 'id' | 'title'> | null
  programCategory?: Pick<ProgramCategory, 'id' | 'label' | 'color'> | null
  programGenre?: Pick<ProgramGenre, 'id' | 'label' | 'color'> | null
  antennaElementType?: Pick<AntennaElementType, 'id' | 'label' | 'color' | 'code'> | null
  collections?:  Pick<Collection, 'id' | 'name' | 'color'>[]
  _count?:       { scheduleItems: number }
  parentProgram?: Pick<Program, 'id' | 'title'> | null
  parts?: Program[]
  partCount?: number
  totalDurationSec?: number
  blockDurationSec?: number
  // Stats AsRun (GET /programs/:id)
  asRunHistory?: AsRunLog[]
  stats?: {
    totalAirings:   number
    avgDriftSec:    number
    gapIncidents:   number
    scheduledCount: number
    completedAirings: number
    rerunCount: number
    firstAiredAt: string | null
    lastAiredAt: string | null
  }
}

export interface Collection {
  id:          string
  channelId:   string
  name:        string
  description: string | null
  color:       string | null
  sortOrder:   number
  active:      boolean
  createdAt:   string
  updatedAt:   string
  _count?:     { programs: number }
  programs?:   Pick<Program, 'id' | 'title' | 'type' | 'durationSec'>[]
}

/**
 * Type d'élément d'antenne personnalisable. Les six types d'origine portent un
 * `code` : c'est la valeur de `ProgramType` que le moteur de diffusion compare.
 * Un type créé par la chaîne n'en a pas et s'appuie sur `JINGLE`.
 */
export interface AntennaElementType {
  id:        string
  channelId: string
  code:      string | null
  label:     string
  color:     string | null
  sortOrder: number
  active:    boolean
  builtin:   boolean
  createdAt: string
  updatedAt: string
  _count?:   { programs: number }
}

export interface ProgramCategory {
  id:        string
  channelId: string
  label:     string
  color:     string | null
  sortOrder: number
  active:    boolean
  createdAt: string
  updatedAt: string
  _count?:   { programs: number; series: number }
}

/**
 * Genre éditorial, liste fermée par chaîne. Seul axe éditorial publié à l'EPG
 * (`<genre>`) : il répond à « de quoi ça parle », là où `ProgramCategory`
 * (le format) répond à « comment c'est produit ». Les fiches le référencent au
 * lieu d'en recopier le libellé, si bien que le renommer met à jour toutes ses
 * fiches d'un coup.
 */
export interface ProgramGenre {
  id:        string
  channelId: string
  label:     string
  color:     string | null
  sortOrder: number
  active:    boolean
  createdAt: string
  updatedAt: string
  _count?:   { programs: number; series: number }
}

/** Motif pour lequel une fiche sélectionnée n'est pas traitée. */
export type BulkSkipReason = 'NOT_FOUND' | 'IS_PART' | 'ALREADY_IN_STATE' | 'HAS_ACTIVE_ITEMS'

/** Libellés miroir du backend (`lib/bulk-program-plan.ts`). */
export const BULK_SKIP_LABELS: Record<BulkSkipReason, string> = {
  NOT_FOUND:        'Introuvable',
  IS_PART:          'Partie d’un programme découpé',
  ALREADY_IN_STATE: 'Déjà dans cet état',
  HAS_ACTIVE_ITEMS: 'À l’antenne',
}

/** Champs qu'une action groupée peut écrire. Absent = non touché ; `null` = vidé. */
export interface BulkProgramChanges {
  programGenreId?:      string | null
  subGenre?:            string | null
  plannedFirstAirDate?: string | null
  seriesId?:            string | null
}

export type BulkProgramAction =
  | { kind: 'UPDATE'; changes: BulkProgramChanges }
  | { kind: 'ARCHIVE' }
  | { kind: 'RESTORE' }
  | { kind: 'ADD_TO_COLLECTION'; collectionId: string }
  | { kind: 'REMOVE_FROM_COLLECTION'; collectionId: string }

export interface BulkProgramRequest {
  channelId?: string
  /**
   * Nom imposé par le garde-fou multi-chaînes du backend, qui ne résout que
   * `programIds` : le renommer ferait sauter la vérification de chaîne.
   */
  programIds: string[]
  action:     BulkProgramAction
  /** `true` : obtenir le plan sans rien écrire. */
  dryRun?:    boolean
}

export interface BulkProgramResult {
  /** Fiches qui seront écrites (ou qui l'ont été). */
  targetIds: string[]
  /** Fiches écartées, avec leur motif. */
  skipped:   { id: string; title: string | null; reason: BulkSkipReason }[]
  /** Nombre réellement écrit — `0` en `dryRun`. */
  applied:   number
}

export interface Series {
  id:           string
  channelId:    string
  title:        string
  titleShort:   string | null
  description:  string | null
  programCode:  string | null
  type:         ProgramType
  programCategoryId: string | null
  /** Genre éditorial (liste fermée), recopié dans chaque nouvelle édition. */
  programGenreId: string | null
  /** Précision libre sous le genre, recopiée avec lui. */
  subGenre:     string | null
  /** Genre hérité en texte libre. Plus saisi ni publié. */
  genre:        string | null
  tags:         string[]
  director:     string | null
  presenter:    string | null
  production:   string | null
  totalSeasons: number | null
  /** `false` : série sans notion de saison/épisode (émissions livrées en lot). */
  numberEpisodes: boolean
  /** `true` : épisodes triés du plus récent au plus ancien dans la fiche série. */
  episodeSortDescending: boolean
  origin:       ProgramOrigin | null
  thumbnailUrl: string | null
  logoUrl:      string | null
  bugType:      BugType
  logoProfileId: string | null
  isFourThree:  boolean
  defaultPartDurationsSec: number[]
  defaultInterPartBreakSec: number
  /** Série livrée en deux versions : durée de la courte (secondes). `null` = une seule version. */
  shortVersionDurationSec?: number | null
  minRerunGapDays: number
  maxRerunsTotal: number | null
  // Valeurs EPG de la série. `epgShowId` est l'`idemission` de Media Press,
  // commun à toutes les éditions : il prime sur celui d'une édition.
  epgFederation: string | null
  epgThemeId:    number | null
  epgShowId:     number | null
  active:       boolean
  createdAt:    string
  updatedAt:    string
  programCategory?: Pick<ProgramCategory, 'id' | 'label' | 'color'> | null
  programGenre?: Pick<ProgramGenre, 'id' | 'label' | 'color' | 'active'> | null
  _count?:      { programs: number }
  programs?:    (Pick<Program, 'id' | 'title' | 'durationSec' | 'interPartBreakSec'> & {
    season: number | null
    episode: number | null
    /** Absent des anciennes réponses : ne compter comme « sans genre » que `null`. */
    programGenreId?: string | null
    mediaAssets?: Array<Pick<MediaAsset, 'status'>>
    parts?: Array<Pick<Program, 'id' | 'title' | 'durationSec' | 'partNumber'> & { mediaAssets?: Array<Pick<MediaAsset, 'status'>> }>
  })[]
}

export type MediaVersion = 'LONG' | 'SHORT'

export interface MediaAsset {
  id:            string
  programId:     string
  filePath:      string
  fileName:      string
  codec:         string | null
  container:     string | null
  durationSec:   number | null
  resolution:    string | null
  frameRate:     number | null
  bitrate:       number | null
  audioLayout:   string | null
  fileSizeBytes: string | null   // bigint sérialisé en string
  isProxy:       boolean
  /** Montage d'un épisode livré en deux versions ; `null` pour un fichier unique. */
  version:       MediaVersion | null
  status:        MediaStatus
  lastChecked:   string | null
  lastError:     string | null
  createdAt:     string
  updatedAt:     string
}

export interface ScheduleGrid {
  id:          string
  channelId:   string
  name:        string
  gridType:    GridType
  weekStart:   string | null
  status:      GridStatus
  version:     number
  createdById: string | null
  createdAt:   string
  updatedAt:   string
  // Relations
  channel?:       Pick<Channel, 'id' | 'name' | 'slug'>
  createdBy?:     Pick<User, 'id' | 'name'> | null
  scheduleItems?: ScheduleItem[]
  _count?:        { scheduleItems?: number; templateSlots?: number; templateGenerations?: number }
  templateSlots?: TemplateSlot[]
  // Méta calculée
  meta?: {
    totalDurationSec: number
    liveCount:        number
    errorCount:       number
    onAirCount:       number
    itemCount:        number
  }
}

export interface TemplateSlot {
  id: string
  templateId: string
  dayOffset: number
  daySpan: number
  timeOfDay: string
  nominalDurationSec: number
  episodeSpacingSec: number
  ruleType: TemplateRuleType
  airingKind: AiringKind
  /** Calage du contenu dans la fenêtre du créneau quand il est plus court. */
  contentAlignment: SlotContentAlignment
  fixedProgramId: string | null
  /** Série de l'inédit produit ou suivi en rediffusion, et vivier d'une
   *  rediffusion libre. */
  seriesId: string | null
  liveSourceId: string | null
  liveStartMode: LiveStartMode
  liveEndMode: LiveEndMode
  /** Habillage ou pub collé avant / après chaque programme du créneau : une
   *  fiche d'habillage ou un spot, jamais les deux sur le même bout. */
  preRollProgramId: string | null
  preRollAdSpotId: string | null
  postRollProgramId: string | null
  postRollAdSpotId: string | null
  minRerunGapDays: number | null
  maxRerunsTotal: number | null
  /** Semaines du mois où le créneau est actif (1 = semaine du 1er du mois),
   *  vide = toutes les semaines. Sert les rotations mensuelles. */
  cycleWeeks: number[]
  /** Rendez-vous d'antenne : l'heure de début est tenue à la seconde. La durée
   *  réelle du contenu faisant foi au dépôt, un créneau ordinaire est décalé
   *  quand ce qui le précède déborde de sa fenêtre ; celui-ci ne l'est jamais. */
  anchored: boolean
  /** Créneau miroir d'une règle MIRROR_RERUN : la génération reprend
   *  l'épisode exact déposé au dernier passage de ce créneau. */
  mirrorSlotId: string | null
  sortOrder: number
  createdAt: string
  updatedAt: string
  fixedProgram?: { id: string; title: string; durationSec: number; type: ProgramType } | null
  series?: { id: string; title: string; minRerunGapDays?: number; maxRerunsTotal?: number | null } | null
  liveSource?: { id: string; name: string; sourceType: SourceType } | null
  preRollProgram?: { id: string; title: string; durationSec: number; type: ProgramType } | null
  postRollProgram?: { id: string; title: string; durationSec: number; type: ProgramType } | null
  preRollAdSpot?: { id: string; title: string; durationSec: number; active: boolean } | null
  postRollAdSpot?: { id: string; title: string; durationSec: number; active: boolean } | null
  /** Suite d'un créneau libre, dans l'ordre d'antenne. */
  entries?: TemplateSlotEntry[]
}

/** Pre-roll ou post-roll résolu : une fiche d'habillage du catalogue, ou un
 *  spot de la régie publicitaire. */
export interface TemplateRollBlock {
  kind: 'PROGRAM' | 'AD_SPOT'
  refId: string
  title: string
  durationSec: number
  itemType: 'AD_BREAK' | 'JINGLE' | 'PROGRAM'
  startTime: string
}

/** Un maillon de la suite choisie à la main pour un créneau libre. */
export interface TemplateSlotEntry {
  programId: string
  sortOrder: number
  program?: { id: string; title: string; durationSec: number; type: ProgramType; season: number | null; episode: number | null }
}

/** Contenu prévu par un créneau du modèle, lisible même quand rien n'a pu être
 *  choisi : c'est ce qui permet de savoir quoi remplacer sur un créneau non
 *  résolu. */
export interface TemplateSlotIntent {
  seriesId: string | null
  seriesTitle: string | null
  fixedProgramId: string | null
  fixedProgramTitle: string | null
  mirrorSlotId: string | null
  /** Créneau suivi par une reprise miroir : son heure et son contenu prévu. */
  mirrorLabel: string | null
  entryTitles: string[]
  /** Résumé prêt à afficher, ou `null` quand le créneau ne nomme aucun contenu. */
  label: string | null
  /** Terme de recherche à proposer d'emblée dans le sélecteur de substitution. */
  searchHint: string | null
}

/** Un créneau du modèle recalé sur la durée réelle de son contenu. */
export interface TemplateRetimeChange {
  slotId: string
  dayOffset: number
  /** Jour, heure et contenu du créneau, prêt à afficher. */
  label: string
  fromTimeOfDay: string
  toTimeOfDay: string
  fromNominalDurationSec: number
  toNominalDurationSec: number
  /** Nombre de programmes que la fenêtre enchaîne, préservé par le recalage. */
  episodes: number
  /** Rendez-vous d'antenne : son heure de début n'a pas bougé. */
  anchored: boolean
  /** La plus longue édition de la série dépasse encore la fenêtre recalée. */
  overflowRisk: boolean
}

/** Ce que le recalage n'a pas pu faire, nommé créneau par créneau. */
export interface TemplateRetimeIssue {
  slotId: string
  dayOffset: number
  label: string
  reason: string
}

export interface TemplateRetimeResult {
  applied: boolean
  changes: TemplateRetimeChange[]
  issues: TemplateRetimeIssue[]
  /** Contrôle du modèle rejoué après écriture ; `null` sur un simple plan. */
  validation: { valid: boolean; issues: string[] } | null
}

export interface TemplateProposal {
  proposalId: string
  slotId: string
  startTime: string
  endTime: string
  dayOffset: number
  ruleType: TemplateRuleType
  itemType: 'PROGRAM' | 'LIVE'
  seriesId: string | null
  seriesTitle: string | null
  intent: TemplateSlotIntent
  liveSourceId: string | null
  liveSourceName: string | null
  liveStartMode: LiveStartMode
  liveEndMode: LiveEndMode
  canCreateEdition: boolean
  /** Bloc d’inédit réservé en tête de créneau : la fiche est créée au dépôt. */
  pendingPremiere: {
    seriesId: string | null
    liveSourceId: string | null
    title: string
    partDurationsSec: number[]
    interPartBreakSec: number
  } | null
  /** Reprise du jour d’un inédit réservé : proposition qui porte sa création. */
  pendingPremiereReplayOf: string | null
  mirrorOfProposalId: string | null
  /** Reprise dont le créneau suivi est arbitré après elle dans la passe : une
   *  substitution posée sur ce créneau la rejoint quand même. */
  substitutionOriginProposalId: string | null
  /** Inédit déjà posé sur la grille repris tel quel (miroir du backend). */
  depositedItemId?: string | null
  /** Rediffusion d'un direct déjà posé, déposée sans média. */
  replaysLiveRecording?: boolean
  programId: string | null
  programTitle: string | null
  durationSec: number
  nominalDurationSec: number
  airingKind: AiringKind
  mediaAssetId: string | null
  mediaLookupDate: string
  reason: string
  rerunCount: number
  firstAiredAt: string | null
  lastAiredAt: string | null
  alternatives: Array<{ id: string; title: string; rerunCount: number; lastAiredAt: string | null }>
  conflicts: Array<{ id: string; title: string; startTime: string; endTime: string; status: string }>
  warnings: string[]
  blocking: boolean
  /** Habillage ou pub collé juste avant / après ce programme. Il suit la même
   *  décision que lui : accepté avec lui, écarté avec lui. */
  preRoll: TemplateRollBlock | null
  postRoll: TemplateRollBlock | null
  /** Jingle de liaison posé devant ce créneau (devant son pre-roll) parce qu'il
   *  touche le créneau précédent du dépôt. Absent des aperçus plus anciens. */
  junctionJingle?: TemplateRollBlock | null
  segments: Array<{
    itemType: 'PROGRAM' | 'LIVE' | 'AD_BREAK' | 'JINGLE'
    partNumber: number | null
    startTime: string
    endTime: string
    durationSec: number
  }>
}

export interface GridGeneration {
  id: string
  templateId: string
  templateVersion: number
  targetGridId: string | null
  targetDate: string
  scope: GenerationScope
  destination: GenerationDestination
  status: GenerationStatus
  appliedAt: string | null
  rolledBackAt: string | null
  createdAt: string
  template?: { name: string }
  targetGrid?: { id: string; name: string } | null
  _count?: { generatedItems: number; replacedItems: number }
}


/** Partie estimée d'un bloc de direct (`lib/livePartPlan.ts`). */
export interface LivePartEstimate {
  id:            string
  /** Rang dans le déroulé, 0-based. Le numéro affiché est `partIndex + 1`. */
  partIndex:     number
  /** Intitulé éditorial ; `null` affiche et publie « Partie N ». */
  title:         string | null
  durationSec:   number
  /** Coupure estimée après cette partie. Ignorée sur la dernière. */
  breakAfterSec: number
  /**
   * Durée ajustée par la régie pendant l'antenne ; `null` = calcul automatique.
   * Ne concerne que le chrono et la Clock : l'EPG publie `durationSec`.
   */
  onAirDurationSec?: number | null
  /** `<id>` publié pour cette partie dans le flux EPG. */
  epgNumber:     number
}

export interface ScheduleItem {
  id:              string
  gridId:          string
  programId:       string | null
  mediaAssetId:    string | null
  liveSourceId:    string | null
  liveStartMode:   LiveStartMode
  liveEndMode:     LiveEndMode
  liveHoldClipId:  string | null
  liveGoLiveAt:    string | null
  interludeClipId: string | null
  startTime:       string          // ISO 8601
  endTime:         string          // ISO 8601
  durationSec:     number
  itemType:        ItemType
  status:          ItemStatus
  sortOrder:       number
  overrides:       Record<string, unknown> | null
  notes:           string | null
  recurrenceId:    string | null
  recurrenceIndex: number | null
  airingKind:       AiringKind
  mediaLookupDate:  string | null
  generationId:     string | null
  templateSlotId:   string | null
  replacedByGenerationId: string | null
  bundleId:        string | null
  bundleOrder:     number | null
  mirrorOriginItemId: string | null
  createdAt:       string
  updatedAt:       string
  // Relations
  program?:        Pick<Program,
    'id' | 'title' | 'titleShort' | 'summary' | 'type' | 'durationSec' | 'genre' | 'tags' |
    'director' | 'presenter' | 'production' | 'year' | 'bugType' | 'logoProfileId' | 'noLogo' | 'isLive' | 'liveSourceId' |
    'programCode' | 'isFourThree' | 'isNew' | 'adHoc' | 'parentProgramId' | 'partNumber' | 'interPartBreakSec' |
    'epgFederation' | 'epgThemeId' | 'epgShowId'
  > & { mediaAssets?: Array<Pick<MediaAsset, 'fileName' | 'filePath'>> } | null
  /** `programId` : fiche du fichier, livrée par la liste de grille (signal « fichier d'une autre émission »). */
  mediaAsset?:     Pick<MediaAsset, 'id' | 'fileName' | 'codec' | 'status' | 'durationSec'> & Partial<Pick<MediaAsset, 'programId'>> | null
  liveSource?:     Pick<LiveSource, 'id' | 'name' | 'sourceType' | 'status'> | null
  interludeClip?:  Pick<InterludeClip, 'id' | 'name' | 'clipType'> | null
  adPlacements?:   Array<{
    id: string
    spot: {
      id: string
      title: string
      filePath: string | null
      durationSec: number
      campaign?: { id?: string; name: string; advertiser?: { name: string } } | null
    }
  }>
  asRunLog?:       Pick<AsRunLog, 'actualStart' | 'actualEnd' | 'durationDiffSec' | 'status'> | null
  /**
   * Déroulé estimé d'un bloc de direct : les durées annoncées de ses parties.
   * Ce sont des annotations, jamais des éléments diffusés — voir
   * `lib/livePartPlan.ts`.
   */
  liveParts?:      LivePartEstimate[]
}

export interface LiveSource {
  id:                  string
  channelId:           string
  name:                string
  sourceType:          SourceType
  connectionString:    string | null
  channelIndex:        number | null
  /** Format SDI forcé (ex. « 1080p5000 »). Null = mode du canal, au risque
   *  d'une image noire silencieuse si la source n'y correspond pas. */
  sdiFormat:           string | null
  status:              SourceStatus
  reconnectAttempts:   number
  reconnectIntervalSec: number
  lastSeen:            string | null
  active:              boolean
  createdAt:           string
  updatedAt:           string
}

export interface InterludeClip {
  id:          string
  channelId:   string
  name:        string
  filePath:    string | null
  durationSec: number | null
  clipType:    InterludeType
  priority:    number
  active:      boolean
  tags:        string[]
  createdAt:   string
  updatedAt:   string
  /** Présence du fichier sur disque. `null` = non suivi (chemin vide ou flux). */
  presence?:   AssetPresenceEntry | null
}

export interface CgOverlay {
  id:           string
  channelId:    string
  templateId:   string | null
  name:         string
  active:       boolean
  templatePath: string
  videoLayer:   number
  cgLayer:      number
  priority:     number
  anchor:       CgOverlayAnchor
  offsetSec:    number
  durationSec:  number | null
  /** Cadence des passages repetes, uniquement pour l'ancrage PROGRAM_DURING. */
  repeatIntervalSec: number | null
  /** Retrait anticipe : secondes avant la fin du programme ou du direct. */
  hideBeforeEndSec: number
  nextProgramMaxDelaySec: number
  /** Programmes concernes : tous, uniquement les cibles, ou tous sauf elles. */
  programScope: CgOverlayProgramScope
  targetSeriesIds:   string[]
  targetProgramIds:  string[]
  targetCategoryIds: string[]
  /** Libelles des cibles, resolus par l'API. */
  programTargets?: CgOverlayProgramTarget[]
  /** Periodes cumulables ; vide : sans limite de date. */
  validityPeriods: CgOverlayValidityPeriod[]
  data:         Record<string, unknown> | null
  style:        Record<string, unknown> | null
  validFrom:    string | null
  validTo:      string | null
  createdAt:    string
  updatedAt:    string
}

export type CgOverlayProgramScope = 'ALL' | 'INCLUDE' | 'EXCLUDE'

/** Periode de validite ; chaque borne ISO est optionnelle. */
export interface CgOverlayValidityPeriod {
  startsAt: string | null
  endsAt:   string | null
}

/** Prochain passage calcule par l'API pour une regle (apercu, rien n'est arme). */
export interface CgOverlayUpcomingPassage {
  itemId:          string
  title:           string | null
  itemStart:       string
  dueAt:           string
  occurrenceCount: number
}
export type CgOverlayTargetKind = 'SERIES' | 'PROGRAM' | 'CATEGORY'

/** Emission (toutes ses editions), fiche isolee ou categorie editoriale. */
export interface CgOverlayProgramTarget {
  kind:    CgOverlayTargetKind
  id:      string
  label:   string
  /** Cible supprimee du catalogue depuis l'enregistrement de la regle. */
  missing: boolean
}

/** Superposition des habillages à l'antenne : en haut = affiché au-dessus. */
export interface CgLayerOrderEntry {
  id: string
  name: string
  cgLayer: number
}

export interface CgLayerOrderResult {
  templates: CgLayerOrderEntry[]
  /** Modèles qui ont changé de couche. */
  moved: number
  /** Habillages déplacés à l'antenne, sans rechargement. */
  swappedOnAir: number
}

export interface CgTemplate {
  revision?: string
  id:           string
  channelId:    string
  name:         string
  templateType: CgTemplateType
  htmlPath:     string
  defaultData:  Record<string, unknown> | null
  active:       boolean
  /** Proposé sous le retour antenne, sur la grille, pour un envoi à la main. */
  manualOnGrid: boolean
  /** Layer CG réservé au modèle. NULL pour les logos, qui partagent celui du bug. */
  cgLayer:      number | null
  createdAt:    string
}

export interface LogoProfile {
  id:            string
  channelId:     string
  key:           string
  name:          string
  templateId:    string | null
  active:        boolean
  /** Proposé sous le retour antenne, sur la grille, pour un envoi à la main. */
  manualOnGrid:  boolean
  sortOrder:     number
  createdAt:     string
  updatedAt:     string
  template?:     CgTemplate | null
}

export interface CgAsset {
  id:           string
  channelId:    string
  name:         string
  fileName:     string
  relativePath: string
  mimeType:     string
  sizeBytes:    number
  usageCount:   number
  /** Enregistré en base mais absent du dossier assets. */
  missing:      boolean
  url:          string
  createdAt:    string
  updatedAt:    string
}

/** Police de la bibliothèque partagée (globale, sans chaîne). */
export interface CgFont {
  id:           string
  name:         string
  fileName:     string
  relativePath: string
  mimeType:     string
  sizeBytes:    number
  /** Nom de famille CSS déterministe (`cgfont-<id>`) à stocker dans un élément. */
  fontFamily:   string
  /** Enregistré en base mais absent du dossier des polices. */
  missing:      boolean
  url:          string
  createdAt:    string
  updatedAt:    string
}

export interface CgDesignerElement {
  id:           string
  type:         CgDesignerElementType
  name:         string
  x:            number
  y:            number
  width:        number
  height:       number
  rotation?:    number
  visible?:     boolean
  locked?:      boolean
  text?:        string
  dataKey?:     string
  imageUrl?:    string
  videoUrl?:    string
  assetId?:     string
  color?:       string
  background?:  string
  fontSize?:    number
  fontWeight?:  number
  fontFamily?:  string
  textAlign?:   'left' | 'center' | 'right'
  letterSpacing?: number
  lineHeight?:  number
  borderRadius?: number
  borderWidth?: number
  borderColor?: string
  opacity?:     number
  shadowColor?: string
  shadowBlur?:  number
  shadowX?:     number
  shadowY?:     number
  // Effets appliqués aux glyphes (texte/compteur), distincts de la box
  textStrokeWidth?: number
  textStrokeColor?: string
  textShadowColor?: string
  textShadowBlur?:  number
  textShadowX?:     number
  textShadowY?:     number
  imageFit?:    'cover' | 'contain' | 'fill'
  videoLoop?:   boolean
  videoPlaybackRate?: number
  videoHoldFrame?: number
  videoFrameRate?: number
  videoResumeBeforeEndMs?: number
  animation?:   CgDesignerAnimation
  enterAnimation?: CgDesignerAnimation
  exitAnimation?: CgDesignerAnimation
  easing?:      CgDesignerEasing
  delayMs?:     number
  durationMs?:  number
  exitDelayMs?: number
  exitDurationMs?: number
  /** Temps d'affichage depuis l'apparition de l'habillage ; 0 : jusqu'au retrait. */
  visibleForMs?: number
  /** Reapparition toutes les N ms (debut a debut) ; 0 : un seul passage. */
  repeatEveryMs?: number
  counterMode?: CgDesignerCounterMode
  counterFormat?: CgDesignerCounterFormat
  counterTarget?: string
  counterPrefix?: string
  counterSuffix?: string
  /** Texte lie a une variable : encadre la valeur, absent quand elle est vide. */
  textPrefix?: string
  textSuffix?: string
  scroll?: boolean
  scrollDirection?: 'left' | 'right'
  scrollSpeedPxSec?: number
  scrollGapPx?: number
}

export interface CgDesignerDesign {
  width:      number
  height:     number
  background?: string
  fps?:       number
  durationMs?: number
  gridSize?:  number
  safeArea?:  number
  elements:   CgDesignerElement[]
}

export interface CgDesignerTemplatePayload {
  expectedRevision?: string
  channelId:   string
  templateId?: string | null
  name:        string
  slug?:       string | null
  design:      CgDesignerDesign
}

export interface CgDesignerTemplateResult {
  template: CgTemplate
  htmlPath: string
}

/** Spot figé d'un bloc pub dans l'instantané as-run. */
export interface AsRunSpot {
  order:          number
  spotTitle:      string
  campaignName:   string | null
  advertiserName: string | null
  durationSec:    number
}

export interface AsRunLog {
  id:              string
  scheduleItemId:  string
  channelId:       string
  scheduledStart:  string
  actualStart:     string | null
  actualEnd:       string | null
  durationDiffSec: number | null
  sourceLabel:     string | null
  status:          AsRunStatus
  metadata:        Record<string, unknown> | null
  createdAt:       string
  spotsSnapshot:   AsRunSpot[] | null
}
export interface PlayoutStateRecord {
  id:            string
  channelId:     string
  currentItemId: string | null
  nextItemId:    string | null
  state:         PlayoutState
  frameCount:    number
  fps:           number
  manualInterlude: boolean
  pendingManualLiveItemId?: string | null
  updatedAt:     string
  casparStatus:    Record<string, unknown> | null
  // Calculé côté service
  bridgeConnected: boolean
  commandQueueLen: number
  serverTime?:      string
  engineInstanceId?: string
  runtimePhase?:    string | null
  recoveryStatus?:  'IDLE' | 'RUNNING' | 'MATCHED' | 'FORCED_PLAY' | 'FAILED'
  casparMediaPath?: string | null
  expectedBugType?: string | null
  activeBugType?:   string | null
  lastLogoEvent?:   string | null
  lastRecoveryEvent?: string | null
  casparFrame?:     number
  casparTimeSec?:   number
  casparFps?:       number
  playoutDriftMs?:  number | null
  lastPlayDeltaMs?: number | null
  lastEndDeltaMs?:  number | null
  runtimeItems?:    Array<Record<string, unknown>>
  activeCgOverlays?: Array<Record<string, unknown>>
  activeTargetId?: string | null
  activeTargetName?: string | null
  autoFailoverArmed?: boolean
  targets?: PlayoutTargetHealth[]
  router?: PlayoutRouterRuntime
}

export interface PlayoutTarget {
  id: string
  channelId: string
  name: string
  host: string
  port: number
  casparChannel: number
  fps: number
  routerInput: number | null
  priority: number
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export interface VideoRouter {
  id: string
  channelId: string
  host: string
  port: number
  outputIndex: number
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export interface PlayoutTargetHealth {
  id: string
  name: string
  role: 'PRIMARY' | 'MIRROR'
  active: boolean
  connected: boolean
  healthy: boolean
  host: string
  port: number
  casparChannel: number
  routerInput: number | null
  frame: number
  mediaPath: string | null
  heartbeatAt: string | null
  probe: { ok: boolean; status: string; code: string | null; latencyMs: number }
  preflight: { checkedAt: string; ok: boolean; missing: string[]; error: string | null } | null
}

export interface PlayoutRouterRuntime {
  enabled: boolean
  connected: boolean
  host: string | null
  outputIndex: number | null
}

export interface PlayoutFailoverEvent {
  id: string
  channelId: string
  fromTargetId: string | null
  toTargetId: string
  trigger: 'AUTO' | 'MANUAL' | 'STARTUP'
  reason: string
  routerAcked: boolean
  triggeredByUserId: string | null
  detail: Record<string, unknown> | null
  createdAt: string
}

export interface RedundancyConfigResponse {
  targets: PlayoutTarget[]
  router: VideoRouter | null
  state: { activeTargetId: string | null; autoFailoverArmed: boolean } | null
  history: PlayoutFailoverEvent[]
  policy: { confirmMs: number; cooldownMs: number; maxSwitchesPerHour: number; failOnFrozenFrames: boolean; heartbeatStaleMs: number }
  runtime: {
    activeTargetId: string | null
    activeTargetName: string | null
    autoFailoverArmed: boolean
    targets: PlayoutTargetHealth[]
    router: PlayoutRouterRuntime
  } | null
}

export interface TimingTraceEntry {
  ts: string
  event: string
  channelId?: string
  itemId?: string
  itemType?: string
  title?: string | null
  commandType?: string
  responseMs?: number
  totalMs?: number
  scheduledStartDeltaMs?: number
  scheduledEndDeltaMs?: number
  durationDiffSec?: number
  playoutDriftMs?: number | null
  [key: string]: unknown
}

export interface WatchdogCheck {
  id:             string
  scheduleItemId: string
  checkedAt:      string
  checkType:      'DURATION_DRIFT' | 'FREEZE' | 'AUDIO_SILENCE' | 'LOAD_FAILURE' | 'SIGNAL_LOST'
  severity:       'OK' | 'WARNING' | 'CRITICAL' | 'FATAL'
  expectedSec:    number | null
  actualSec:      number | null
  driftSec:       number | null
  audioLevelDb:   number | null
  actionTaken:    'NONE' | 'INTERLUDE' | 'SKIP' | 'RECALC' | 'FALLBACK' | 'ALERT'
  createdAt:      string
}

export interface RecurrenceRule {
  id:            string
  channelId:     string
  gridId:        string
  programId:     string | null
  mediaAssetId:  string | null
  liveSourceId:  string | null
  itemType:      ItemType
  durationSec:   number
  frequency:     RecurrenceFreq
  interval:      number
  daysOfWeek:    number[]
  daysOfMonth:   number[]
  timeOfDay:     string
  startDate:     string
  endDate:       string | null
  maxCount:      number | null
  createdAt:     string
  updatedAt:     string
  _count?:       { items: number }
  items?:        ScheduleItem[]
}

export interface AiSuggestedProgram {
  type:         'PROGRAM'
  programId:    string
  programTitle: string
  startTime:    string
  endTime:      string
  durationSec:  number
  reason:       string
  confidence:   number
}

export interface AiSuggestedAd {
  type:           'AD'
  spotId:         string
  spotTitle:      string
  campaignName:   string
  scheduleItemId: string
  startTime:      string
  reason:         string
  confidence:     number
}

export type AiSuggestedItem = AiSuggestedProgram | AiSuggestedAd

export interface AiSuggestion {
  id:          string
  channelId:   string
  gridId:      string
  date:        string
  phase:       AiSuggestionPhase
  status:      AiSuggestionStatus
  suggestions: AiSuggestedItem[]
  appliedAt:   string | null
  createdAt:   string
}

export interface CreateRecurrencePayload {
  channelId:    string
  gridId:       string
  programId?:   string
  mediaAssetId?: string
  liveSourceId?: string
  itemType:     ItemType
  durationSec:  number
  frequency:    RecurrenceFreq
  interval:     number
  daysOfWeek:   number[]
  daysOfMonth:  number[]
  timeOfDay:    string
  startDate:    string
  endDate?:     string
  maxCount?:    number
}

export interface User {
  id:                  string
  username:            string
  email:               string
  name:                string
  role:                UserRole
  active:              boolean
  mustChangePassword:  boolean
  passwordChangedAt:   string | null
  permissionOverrides: Partial<Record<Permission, boolean>>
  totpEnabledAt:       string | null
  failedLoginCount:    number
  lockedUntil:         string | null
  lastLogin:           string | null
  createdAt:           string
  updatedAt:           string
}

// ─── Réponses API paginées ────────────────────────────────────────────────────

export interface PaginatedResponse<T> {
  data: T[]
  meta: {
    total:      number
    page:       number
    limit:      number
    totalPages: number
  }
}

export interface ReplaceProgramRangeResult {
  sourceProgramId: string
  targetProgramId: string
  targetProgramTitle: string
  replacedCount: number
  skippedCount: number
  gridIds: string[]
  from: string
  to: string
}

/**
 * Pourquoi la boucle de rediffusion s'est arrêtée là :
 * `COMPLETE` elle atteint l'heure demandée pile · `WINDOW` la copie suivante
 * aurait débordé · `CONFLICT` elle serait tombée sur un élément déjà posé ·
 * `CAPPED` le plafond de sécurité a été atteint.
 */
export type RebroadcastStopReason = 'COMPLETE' | 'WINDOW' | 'CONFLICT' | 'CAPPED'

/**
 * `EXPLICIT` part de l'heure saisie · `AFTER_LAST_ITEM` s'accroche à la fin de
 * ce qui est déjà posé à la suite de la sélection.
 */
export type RebroadcastStartMode = 'EXPLICIT' | 'AFTER_LAST_ITEM'

export interface RebroadcastRequest {
  itemIds: string[]
  targetStart: string
  repeatUntil: string
  replaceExisting?: boolean
  /**
   * `AFTER_LAST_ITEM` ignore `targetStart` : le serveur accroche la boucle à la
   * fin de ce qui est déjà posé derrière la sélection, pour enchaîner sans trou.
   */
  startMode?: RebroadcastStartMode
  /** Autorise une dernière boucle écourtée pour finir au plus près de l'heure. */
  partialTail?: boolean
  /** Cadence globale entre deux horaires repères, en secondes. */
  cadenceSec?: number
  jingleReplacements?: Array<{ sourceItemId: string; programId: string }>
  jingleRemovals?: Array<{ sourceItemId: string }>
  jingleInsertions?: Array<{
    afterProgramItemId: string | null
    beforeProgramItemId: string | null
    position: 'AFTER_PROGRAM' | 'BEFORE_PROGRAM'
    programId: string
  }>
  adInsertions?: Array<{
    afterProgramItemId: string
    beforeProgramItemId: string
    spotIds: string[]
  }>
  breakAdjustments?: Array<{
    afterProgramItemId: string | null
    beforeProgramItemId: string | null
    infoserviceDurationSec: number
  }>
  /** Simule sans rien écrire : c'est l'aperçu affiché avant validation. */
  dryRun?: boolean
}

export interface RebroadcastReport {
  dryRun: boolean
  sourceStart: string
  sourceEnd: string
  sourceDurationSec: number
  sourceItemCount: number
  adjustedBreaks: number
  replacedJingles: number
  removedJingles: number
  insertedJingles: number
  insertedAdBreaks: number
  insertedAdSpots: number
  infoserviceDurationSec: number
  /** Cadence globale retenue, ou `null` en mode historique. */
  cadenceSec: number | null
  /** Durée totale d'infoservice ajoutée pour préserver les repères. */
  cadencePaddingSec: number
  /** Début réellement retenu — l'accrochage a pu le faire glisser. */
  targetStart: string
  requestedEnd: string
  occurrences: number
  /** Horaires de départ retenus après contrôle des conflits. */
  occurrenceStarts: string[]
  createdItems: number
  replacedItems: number
  /** La dernière boucle est écourtée pour serrer l'heure de fin demandée. */
  partialTail: boolean
  /** Éléments repris par cette dernière boucle, sur `sourceItemCount`. */
  partialItemCount: number
  /** Fin réelle de la dernière rediffusion, `null` si aucune ne tenait. */
  endsAt: string | null
  /** Temps laissé libre entre cette fin réelle et l'heure demandée. */
  shortfallSec: number
  truncated: boolean
  stopReason: RebroadcastStopReason
  blockedAt: string | null
  gridIds: string[]
}

// ─── Paramètres de requête ────────────────────────────────────────────────────

export interface ProgramQuery {
  channelId?:    string
  type?:         ProgramType
  search?:       string
  tags?:         string
  isNew?:        'true' | 'false'
  collectionId?: string
  seriesId?:     string
  page?:         number
  limit?:        number
  /** `premiereDate` : date de première diffusion (prévue, sinon constatée). */
  sortBy?:       'title' | 'createdAt' | 'durationSec' | 'type' | 'premiereDate'
  sortDir?:      'asc' | 'desc'
}

export interface GridQuery {
  channelId?: string
  gridType?:  GridType
  weekStart?: string
  page?:      number
  limit?:     number
}

// ─── Payloads de création / mise à jour ──────────────────────────────────────

export type CreateProgramPayload = Omit<Program,
  'id' | 'createdAt' | 'updatedAt' | 'mediaAssets' | 'channel' | '_count' | 'asRunHistory' | 'stats'
>

export type UpdateProgramPayload = Partial<Omit<CreateProgramPayload, 'channelId'>>

export interface CreateGridPayload {
  channelId: string
  name:      string
  gridType?: GridType
  weekStart?: string
}

export interface CreateItemPayload {
  gridId:           string
  startTime:        string
  durationSec:      number
  itemType:         ItemType
  programId?:       string
  mediaAssetId?:    string
  liveSourceId?:    string
  interludeClipId?: string
  sortOrder?:       number
  overrides?:       Record<string, unknown>
  notes?:           string
}

export interface MoveItemPayload {
  startTime: string
}

// ─── Messages WebSocket ───────────────────────────────────────────────────────

export interface WsAlert {
  type:     string
  severity: 'OK' | 'WARNING' | 'CRITICAL' | 'ERROR' | 'FATAL'
  itemId?:  string
  message:  string
  ts:       string
}

export type WsMessage =
  | { type: 'STATE_INIT';     data: PlayoutStateRecord }
  | { type: 'PLAYOUT_UPDATE'; data: Partial<PlayoutStateRecord> }
  | { type: 'WATCHDOG_ALERT'; data: WsAlert }
  | { type: 'ITEM_STATUS';    itemId: string; status: ItemStatus }
  | { type: 'GRID_UPDATED';   gridId: string }
  | { type: 'GRID_OVERLAP';    gridId: string; itemId: string; overItemId: string; overlapSec: number; startTime: string; message: string }
  | { type: 'LOGO_PROFILES_UPDATED'; channelId?: string }
  | { type: 'PLAYOUT_FAILOVER'; data: Record<string, unknown> }
  | { type: 'PLAYOUT_TARGETS_UPDATE'; data: Partial<PlayoutStateRecord> }
  | { type: 'PING' }
  | { type: 'ACK';            ref: string }
  | { type: 'ERROR';          message: string }

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Durée en secondes → "HH:MM:SS" */
export function formatDuration(sec: number): string {
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/** Durée en secondes → "52min" ou "1h30" */
export function formatDurationShort(sec: number): string {
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  if (h > 0 && m > 0) return `${h}h${String(m).padStart(2, '0')}`
  if (h > 0) return `${h}h`
  return `${m}min`
}

/** Retourne true si un item a un problème bloquant */
export function itemHasError(item: ScheduleItem): boolean {
  return (
    item.status === 'ERROR' ||
    item.mediaAsset?.status === 'MISSING' ||
    item.mediaAsset?.status === 'INVALID_CODEC'
  )
}

/** Couleur sémantique d'un item selon son statut */
export function itemColor(item: ScheduleItem): string {
  if (item.status === 'ERROR')  return 'error'
  if (item.status === 'ON_AIR') return 'onair'
  if (item.status === 'DONE')   return 'done'
  if (item.status === 'GAP')    return 'interlude'
  if (item.itemType === 'LIVE') return 'live'
  if (item.itemType === 'AD_BREAK' || item.itemType === 'JINGLE') return 'ad'
  if (item.itemType === 'INFOSERVICE') return 'interlude'
  if (item.itemType === 'INTERLUDE' || item.itemType === 'FILLER') return 'interlude'
  return 'program'
}

export type Permission =
  | 'GRID_VIEW' | 'GRID_ITEM_CREATE' | 'GRID_ITEM_EDIT' | 'GRID_ITEM_MOVE'
  | 'GRID_ITEM_DELETE' | 'GRID_RECURRENCE_MANAGE' | 'GRID_LIFECYCLE_MANAGE'
  | 'GRID_CLEAR' | 'GRID_AI_MANAGE' | 'GRID_TEMPLATE_VIEW' | 'GRID_TEMPLATE_MANAGE'
  | 'PROGRAM_VIEW' | 'PROGRAM_MANAGE' | 'PROGRAM_DELETE'
  | 'JINGLE_VIEW' | 'JINGLE_MANAGE' | 'JINGLE_DELETE' | 'CATALOG_TAXONOMY_MANAGE'
  | 'CG_VIEW' | 'CG_MANAGE' | 'CLOCK_VIEW' | 'CLOCK_MANAGE' | 'ADS_VIEW' | 'ADS_MANAGE'
  | 'REPORTS_VIEW_EXPORT' | 'TECHNICAL_SETTINGS_MANAGE' | 'PLAYOUT_CONTROL'

export type AuthStatus =
  | 'PASSWORD_CHANGE_REQUIRED'
  | 'MFA_ENROLLMENT_REQUIRED'
  | 'MFA_CHALLENGE_REQUIRED'
  | 'AUTHENTICATED'

export interface AuthUser {
  id: string
  username: string
  email: string
  name: string
  role: UserRole
  permissions: Permission[]
  channels: Array<{ id: string; name: string; slug: string }>
  mustChangePassword: boolean
  totpEnabled: boolean
}

export interface AuthResponse {
  status: AuthStatus
  user: AuthUser
  /** Durée de dispense du TOTP proposée sous le code ; 0 = mémorisation désactivée. */
  trustedDeviceDays?: number
}

export interface ManagedUser {
  id: string
  username: string
  email: string
  name: string
  role: UserRole
  active: boolean
  mustChangePassword: boolean
  permissionOverrides: Partial<Record<Permission, boolean>>
  channelIds: string[]
  channels: Array<{ id: string; name: string; slug: string }>
  mfaEnabled: boolean
  lastLogin: string | null
  createdAt: string
  updatedAt: string
}

// ─── Suivi de présence des données ────────────────────────────────────────────

/**
 * Familles de données porteuses d'un fichier sur disque. Doit rester aligné sur
 * l'enum `AssetFamily` du schéma Prisma.
 */
export type AssetFamily =
  | 'PROGRAM_MEDIA' | 'AD_SPOT' | 'INTERLUDE_CLIP'
  | 'CG_ASSET' | 'CG_TEMPLATE' | 'BUG_LOGO' | 'THUMBNAIL'

/** `UNKNOWN` = racine de stockage non montée, donc rien de concluant. */
export type AssetPresenceStatus = 'PRESENT' | 'MISSING' | 'UNKNOWN'

export interface AssetPresenceEntry {
  id:            string
  family:        AssetFamily
  entityId:      string
  field:         string
  label:         string
  channelId:     string | null
  expectedPath:  string
  resolvedPath:  string
  status:        AssetPresenceStatus
  lastChecked:   string
  lastPresentAt: string | null
  missingSince:  string | null
  missingStreak: number
  /** Absence suffisamment établie pour que la purge soit proposée. */
  purgeable:     boolean
}

export interface AssetPresenceFamilyCount {
  family:    AssetFamily
  tracked:   number
  missing:   number
  unknown:   number
  purgeable: number
}

export interface DataHealthSummary {
  checkedAt: string | null
  totals:    { tracked: number; present: number; missing: number; unknown: number; purgeable: number }
  byFamily:  AssetPresenceFamilyCount[]
  /** Racines de stockage introuvables : tout ce qui en dépend est indéterminé. */
  unmountedRoots: string[]
  purgeMinStreak: number
  purgeMinAgeMs:  number
}

export type AssetPurgeAction = 'DELETED' | 'DEACTIVATED' | 'FIELD_CLEARED' | 'SKIPPED'

export interface AssetPurgeOutcome {
  id:     string
  family: AssetFamily
  label:  string
  action: AssetPurgeAction
  reason: string | null
}

export interface AssetPurgeResult {
  outcomes: AssetPurgeOutcome[]
  purged:   number
  skipped:  number
}

export interface AssetPresenceScanReport {
  checked:        number
  present:        number
  missing:        number
  unknown:        number
  pruned:         number
  probed:         number
  awaitingProbe:  number
  unmountedRoots: string[]
}


// ─── Installation, transfert et maintenance ─────────────────────────────────
export type InstallationStatus = 'PENDING' | 'DRAFT' | 'APPLYING' | 'SECURING_ADMIN' | 'RESTORING' | 'ACTIVATING' | 'READY' | 'FAILED'
export interface SetupPlayoutTarget { name: string; host: string; port: number; casparChannel: number; fps: number; priority: number; routerInput?: number | null; enabled: boolean }
export interface SetupVideoRouter { host: string; port: number; outputIndex: number; enabled: boolean; autoFailoverArmed: boolean }
export interface SetupDraft {
  mode?: 'scratch' | 'restore'; mediaPath?: string; templatesPath?: string
  channelName?: string; channelSlug?: string
  categories?: Array<{ label: string; active: boolean; sortOrder: number }>
  targets?: SetupPlayoutTarget[]; router?: SetupVideoRouter | null; importPath?: string
}
export interface StorageProbeResult { paths: Array<{ path: string; readable: boolean; writable: boolean; containerVisible: boolean; freeBytes: number }> }
export interface BackupManifestSummary {
  formatVersion: number; createdAt: string
  files: Array<{ path: string; size: number; sha256: string }>
  summary?: { channels?: Array<{ name: string; slug: string; playoutTargets?: Array<{ name: string; host: string; port: number; enabled: boolean }> }> }
}
export type MaintenanceJobState = 'QUEUED' | 'PREFLIGHT' | 'RUNNING' | 'WAITING_SAFE_WINDOW' | 'RESTARTING' | 'VERIFYING' | 'ROLLING_BACK' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED'
export interface MaintenanceJob {
  id: string; type: 'GIT_STATUS' | 'BACKUP_EXPORT' | 'BACKUP_VERIFY' | 'RESTORE' | 'UPDATE' | 'STORAGE_PROBE' | 'ACTIVATE_PLAYOUT' | 'DEACTIVATE_PLAYOUT'
  state: MaintenanceJobState; progress: number; stage?: string | null; cancellable: boolean
  result?: Record<string, unknown> | null; error?: { code: string; message: string; recovery?: string } | null
  createdAt: string; updatedAt: string
}
export interface UpdateBlocker {
  itemId: string; channelId: string; channelName: string; title: string; itemType: string
  status: ItemStatus; startTime: string; endTime: string
}
export interface UpdateSafety { safe: boolean; checkedAt: string; guardSeconds: number; blockers: UpdateBlocker[] }
export interface UpdateAvailability {
  state: 'CHECKING' | 'CURRENT' | 'AVAILABLE' | 'BLOCKED' | 'OFFLINE' | 'RUNNING'
  jobId?: string; controllerAvailable: boolean; branch?: string; localCommit?: string; remoteCommit?: string
  clean?: boolean; ahead?: number; behind?: number; divergent?: boolean; summary?: string; date?: string
  safety: UpdateSafety
}

// ─── Clock — contrats reproduits à l'identique dans types/index.ts ──────────
export type ClockDisplayKind = 'CONTROL_ROOM' | 'STUDIO'
export type ClockTheme = 'LIGHT' | 'DARK'
export type ClockTimePrecision = 'SECONDS' | 'TENTHS' | 'HUNDREDTHS' | 'MILLISECONDS'
export type ClockColorRole = 'DEFAULT' | 'MUTED' | 'ACCENT' | 'SUCCESS' | 'WARNING' | 'DANGER' | 'ON_AIR'
export type ClockWidgetType = 'CLOCK' | 'DATE' | 'ON_AIR' | 'REMAINING' | 'ELAPSED' | 'NEXT_ITEM' | 'NEXT_LIVE' | 'TOP' | 'LIVE_END' | 'LIVE_PART' | 'PROGRESS' | 'RUNDOWN' | 'VIDEO_RETURN' | 'LIVE_SOURCE' | 'SYSTEM_STATUS' | 'ALERTS' | 'TEXT' | 'CHANNEL_IDENTITY'
/** Ce que devient un texte trop long pour sa boîte. */
export type ClockOverflowMode = 'SHRINK' | 'WRAP' | 'CLIP'
export interface ClockWidget {
  id: string; type: ClockWidgetType; name: string
  x: number; y: number; width: number; height: number; visible: boolean; locked: boolean
  style: { align: 'LEFT' | 'CENTER' | 'RIGHT'; size: 'SM' | 'MD' | 'LG' | 'XL' | 'HERO'; weight: 'REGULAR' | 'MEDIUM' | 'BOLD'; surface: 'NONE' | 'SUBTLE' | 'SOLID'; colorRole: ClockColorRole; showLabel: boolean; valueScale?: number; labelScale?: number; titleScale?: number; overflow?: ClockOverflowMode }
  config: { label?: string; text?: string; format?: 'MM_SS' | 'HH_MM_SS' | 'ABSOLUTE'; timePrecision?: ClockTimePrecision; warningSec?: number; dangerSec?: number; endWarningSec?: number; endDangerSec?: number; tenthsBelowSec?: number; itemCount?: number; missingBehavior?: 'HIDE' | 'DASH' | 'MESSAGE'; missingMessage?: string }
}
export interface ClockLayoutConfig { schemaVersion: 1; width: 1920; height: 1080; gridSize: 8; safeAreaPct: 5; backgroundRole: ClockColorRole; widgets: ClockWidget[] }
export interface ClockTemplate { id: string; channelId: string; name: string; description: string | null; displayKind: ClockDisplayKind; defaultTheme: ClockTheme; systemKey: string | null; draftConfig: ClockLayoutConfig; publishedConfig: ClockLayoutConfig | null; publishedVersion: number; publishedAt: string | null; createdAt: string; updatedAt: string; _count?: { displays: number } }
export interface ClockDisplay { id: string; channelId: string; templateId: string; name: string; locationLabel: string | null; displayKind: ClockDisplayKind; themeOverride: ClockTheme | null; audibleAlerts: boolean; active: boolean; accessTokenHint: string; lastSeenAt: string | null; createdAt: string; updatedAt: string; template?: { id: string; name: string; publishedVersion: number } }
export interface ClockBootstrap { display: ClockDisplay; channel: Channel; template: { id: string; name: string; displayKind: ClockDisplayKind; defaultTheme: ClockTheme; config: ClockLayoutConfig; version: number; publishedAt: string } | null; items: ScheduleItem[]; playout: Record<string, unknown> | null; serverTime: string; wsTicket: string }
export interface ClockDisplaySecret { display: ClockDisplay; token: string; launchUrl: string }
